/**
 * IBAN-Logik für die Auszahlung per Überweisung (Plan Welle 3, W3-Auszahlung): Normalisieren, Prüfen (ISO 13616, MOD 97-10),
 * Formatieren, Maskieren und Kontoinhaber-Prüfung. Nur IBANs aus dem EWR (Entscheidung 8): Dort ist für SEPA keine BIC nötig,
 * Formular, GiroCode und SEPA-Datei bleiben einfach. Reine Funktionen ohne Seiteneffekte, für Server und Web.
 */

/** Länge der IBAN je Land des EWR (EU-Staaten, Island, Liechtenstein, Norwegen). */
export const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  AT: 20,
  BE: 16,
  BG: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  EE: 20,
  ES: 24,
  FI: 18,
  FR: 27,
  GR: 27,
  HR: 21,
  HU: 28,
  IE: 22,
  IS: 26,
  IT: 27,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MT: 31,
  NL: 18,
  NO: 15,
  PL: 28,
  PT: 25,
  RO: 24,
  SE: 24,
  SI: 19,
  SK: 24,
};

/** Längste IBAN im EWR (Malta); Grenze für Eingaben. */
export const IBAN_MAX_LENGTH = 31;

/** Größte Zeichenzahl eines Kontoinhabers (Feld `Nm` in der SEPA-Datei und im GiroCode). */
export const HOLDER_NAME_MAX_LENGTH = 70;

/** Entfernt Leerzeichen und Trennstriche, großgeschrieben. Prüft nichts. */
export function normalizeIban(input: string): string {
  return input.replace(/[\s\u00a0-]+/g, '').toUpperCase();
}

/** Rest der Ganzzahldivision durch 97 für eine IBAN in der Rechenreihenfolge (Ziffern, Buchstaben als 10 bis 35). */
function mod97(numeric: string): number {
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder;
}

/** Buchstaben als Zahlen 10 bis 35, Ziffern bleiben. */
function toNumeric(text: string): string {
  let out = '';
  for (const char of text) {
    const code = char.charCodeAt(0);
    out += code >= 65 && code <= 90 ? String(code - 55) : char;
  }
  return out;
}

/** Prüfziffern (zwei Ziffern) für Land und BBAN, nach ISO 13616. Für Tests und Beispiele. */
export function ibanCheckDigits(country: string, bban: string): string {
  const rest = mod97(toNumeric(`${bban}${country}00`));
  return String(98 - rest).padStart(2, '0');
}

/**
 * Ist das eine gültige IBAN aus dem EWR? Prüft die Zeichen (Land, zwei Prüfziffern, höchstens 27 Buchstaben oder Ziffern), die
 * Länge des Landes und die Prüfziffer (MOD 97-10). Erwartet eine bereits normalisierte IBAN (`normalizeIban`).
 */
export function isValidIban(iban: string): boolean {
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,27}$/.test(iban)) return false;
  const expected = IBAN_LENGTHS[iban.slice(0, 2)];
  if (expected === undefined || iban.length !== expected) return false;
  return mod97(toNumeric(`${iban.slice(4)}${iban.slice(0, 4)}`)) === 1;
}

/** Normalisiert und prüft in einem Schritt: die IBAN oder `null`. */
export function parseIban(input: string): string | null {
  const iban = normalizeIban(input);
  return isValidIban(iban) ? iban : null;
}

/** Vierergruppen mit Leerzeichen, z. B. „DE89 3704 0044 0532 0130 00“. */
export function formatIban(iban: string): string {
  return normalizeIban(iban)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

const MASK_CHAR = '•';

/**
 * Anzeige ohne die Kontonummer: Land, Prüfziffer und die letzten zwei Zeichen bleiben sichtbar, alles dazwischen sind Punkte,
 * in Vierergruppen, z. B. „DE89 •••• •••• •••• •••• 30“. Mehr zeigt keine Oberfläche außer dem Inhaber selbst.
 */
export function maskIban(iban: string): string {
  const normalized = normalizeIban(iban);
  if (normalized.length < 8) return MASK_CHAR.repeat(normalized.length);
  const masked = `${normalized.slice(0, 4)}${MASK_CHAR.repeat(normalized.length - 6)}${normalized.slice(-2)}`;
  return masked.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Steuerzeichen, Zeilen- und Absatztrenner sowie unsichtbare Formatzeichen (Richtungs- und Nullbreitenzeichen) als Bereiche
 * von Codepunkten. Die Zeichenklasse entsteht daraus, damit im Quelltext keine unsichtbaren Zeichen stehen.
 */
const UNSAFE_RANGES: readonly (readonly [number, number])[] = [
  [0x0000, 0x001f],
  [0x007f, 0x009f],
  [0x00ad, 0x00ad],
  [0x061c, 0x061c],
  [0x180e, 0x180e],
  [0x200b, 0x200f],
  [0x2028, 0x202e],
  [0x2060, 0x206f],
  [0xfeff, 0xfeff],
  [0xfff9, 0xfffb],
];
const unsafeClass = UNSAFE_RANGES.map(
  ([from, to]) => `\\u{${from.toString(16)}}-\\u{${to.toString(16)}}`,
).join('');
const UNSAFE_CHARACTER = new RegExp(`[${unsafeClass}]`, 'u');
const UNSAFE_CHARACTERS = new RegExp(`[${unsafeClass}]`, 'gu');

/**
 * Darf der Text als Kontoinhaber gespeichert werden? Höchstens 70 Zeichen, nicht leer, keine Steuerzeichen und keine Zeilenumbrüche
 * (sonst könnte „Max\nDE…“ im GiroCode eine fremde IBAN in die nächste Zeile schieben). Geprüft wird der Text, wie er ankommt:
 * `trim` und Normalisierung macht der Aufrufer vorher (`cleanHolderName`).
 */
export function isSafeHolderName(name: string): boolean {
  if (name.trim() === '' || name !== name.trim()) return false;
  if (Array.from(name).length > HOLDER_NAME_MAX_LENGTH) return false;
  return !UNSAFE_CHARACTER.test(name);
}

/**
 * Macht aus beliebigem Text eine einzige Zeile: Steuerzeichen und Zeilenumbrüche werden zu Leerzeichen, Leerräume gefaltet,
 * höchstens `max` Zeichen. Für Ausgaben (GiroCode, SEPA-Datei), nie als Ersatz der Prüfung bei der Eingabe.
 */
export function singleLineText(text: string, max: number): string {
  const folded = text.replace(UNSAFE_CHARACTERS, ' ').replace(/\s+/g, ' ').trim();
  return Array.from(folded).slice(0, max).join('').trim();
}

/** Kontoinhaber für Ausgaben: eine Zeile, höchstens 70 Zeichen. */
export function cleanHolderName(name: string): string {
  return singleLineText(name, HOLDER_NAME_MAX_LENGTH);
}
